import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { UploadError } from '@ff-os/shared-react/components/file-uploader/FileUploader';
import { StepsWindow } from '@ff-os/shared-react/components/window/StepsWindow';
import { StepsWindowStep } from '@ff-os/shared-react/components/window/StepsWindowStep';
import { useMobile } from '@ff-os/shared-react/hooks/useMobile';
import { Translate } from '@ff-os/shared-utils/types/Translate';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import cuid from 'cuid';
import { selectUrlForPhotoById } from '../../_redux-photos';
import { ChangePhotoVariantMethod } from '../../thunks/changePhotoVariant';
import { ChangeVariantConfiguration, saveVariant } from '../../thunks/saveVariant';
import { ChangeBorderStep } from './steps/ChangeBorderStep';
import { ChangeMethodStep } from './steps/ChangeMethodStep';
import { ChangePaperStep } from './steps/ChangePaperStep';
import { ChangeSizeStep } from './steps/ChangeSizeStep';
import { UploadMethodsStep } from './steps/UploadMethodsStep';
import { useVariantStepper } from './useVariantStepper';
import styles from './ChangeVariantWindow.module.scss';

const _ChangeVariantWindow = ({
    title, withMethod, withUploader,
    close, imageUrl, groupId, photoId,
    method: initialMethod, sizeId: initialSizeId, paperId: initialPaperId, borderId: initialBorderId,
    commit, onSave
}: ChangeVariantWindowProps) => {

    const { t } = useTranslation();
    const isMobile = useMobile();

    const [method, setMethod] = useState<ChangePhotoVariantMethod | undefined>(initialMethod);
    const [sizeId, setSizeId] = useState<string | undefined>(initialSizeId);
    const [paperId, setPaperId] = useState<string | undefined>(initialPaperId);
    const [borderId, setBorderId] = useState<string | undefined>(initialBorderId);

    const [withPaperStep, setWithPaperStep] = useState<boolean>(false);
    const [withBorderStep, setWithBorderStep] = useState<boolean>(false);

    const localGroupId = useMemo(() => {
        return groupId ?? cuid();
    }, [groupId]);

    const save = useCallback((files?: File[]) => {
        if (paperId && sizeId) {
            if (withBorderStep && !borderId) {
                return; 
            }
            const config = { method, paperId, sizeId, groupId: localGroupId, photoId, borderId };
            commit(config, files);
            close();
            onSave && onSave(config);
        }
    }, [close, commit, localGroupId, method, onSave, paperId, photoId, sizeId, borderId, withBorderStep]);

    const { ref, Stepper } = useVariantStepper(
        method, sizeId, paperId, borderId, withPaperStep, withBorderStep, withUploader, withMethod);

    return (
        <StepsWindow ref={ref}
            close={close}
            className={isMobile ? styles.mobile : styles.desktop}
            fullHeight={isMobile} fullWidth={isMobile}>

            {withMethod && (
                <StepsWindowStep
                    title={t(title ?? 'photo_prints.change_variant')}
                    backButtonText={t('commons.cancel')}
                    forwardButtonText={t('photo_prints.options.method.next')}
                    forwardDisabled={!method}>
                    <ChangeMethodStep imageUrl={imageUrl} value={method} onChange={setMethod} />
                </StepsWindowStep>
            )}

            <StepsWindowStep
                title={t('photo_prints.options.size.title')}
                backButtonText={withMethod ? t('commons.previous') : t('commons.cancel')}
                onForward={() => onSizeStepForward(withPaperStep, withUploader, save)}
                forwardButtonText={getSizeNextStepText(withPaperStep, withUploader, t)}
                forwardDisabled={!sizeId}>
                <ChangeSizeStep
                    imageUrl={imageUrl}
                    onChange={setSizeId}
                    setPaperId={setPaperId}
                    setShowPaperStep={setWithPaperStep}
                    setBorderId={setBorderId}
                    setShowBorderStep={setWithBorderStep}
                    sizeId={sizeId}
                    paperId={paperId}
                    stepper={() => <Stepper current='photo_prints.options.size.name' />} />
            </StepsWindowStep>

            {withPaperStep && (
                <StepsWindowStep
                    title={t('photo_prints.options.paper.title')}
                    backButtonText={t('commons.previous')}
                    onForward={() => !withUploader && save()}
                    forwardButtonText={getPaperNextStepText(withBorderStep, withUploader, t)}
                    forwardDisabled={!paperId}>
                    <ChangePaperStep
                        imageUrl={imageUrl}
                        onChange={setPaperId}
                        setBorderId={setBorderId}
                        setShowBorderStep={setWithBorderStep}
                        sizeId={sizeId}
                        paperId={paperId}
                        stepper={() => <Stepper current='photo_prints.options.paper.name' />} />
                </StepsWindowStep>
            )}
            
            {withBorderStep && (
                <StepsWindowStep
                    title={t('photo_prints.options.border.title')}
                    backButtonText={t('commons.previous')}
                    onForward={() => !withUploader && save()}
                    forwardButtonText={!withUploader ? t('commons.save') : t('commons.next')}
                    forwardDisabled={!borderId}>
                    <ChangeBorderStep
                        imageUrl={imageUrl}
                        onChange={setBorderId}
                        sizeId={sizeId}
                        paperId={paperId}
                        borderId={borderId}
                        stepper={() => <Stepper current='photo_prints.options.border.name' />} />
                </StepsWindowStep>
            )}

            {withUploader && (
                <StepsWindowStep
                    title={t('photo_prints.add')}
                    backButtonText={t('commons.previous')}
                    forwardButtonText={t('commons.save')}
                    forwardDisabled>
                    <UploadMethodsStep
                        groupId={localGroupId}
                        close={close} save={save}
                        stepper={() => <Stepper current='photo_prints.upload' />} />
                </StepsWindowStep>
            )}
        </StepsWindow>
    );
};

type OwnProps = Partial<ChangeVariantConfiguration> & {

    title?: string;

    forceNewGroup?: boolean;
    withMethod?: boolean;
    withUploader?: boolean;

    close: () => void;
    onSave?: (config: ChangeVariantConfiguration) => void;
    onFileUploadError?: (errors: UploadError[]) => void;
};

export type ChangeVariantWindowProps = OwnProps & {
    imageUrl?: string;
    commit: (config: ChangeVariantConfiguration, files?: File[]) => void;
};

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => ({
    imageUrl: selectUrlForPhotoById(state, ownProps.photoId!)
});

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, { forceNewGroup }: OwnProps) => ({
    commit: (config: ChangeVariantConfiguration, files?: File[]) => dispatch(saveVariant(config, files, forceNewGroup))
});

export const ChangeVariantWindow = connect(
    mapStateToProps,
    mapDispatchToProps
)(_ChangeVariantWindow);

const onSizeStepForward = (
    withPaperStep: boolean,
    withUploader: boolean | undefined,
    save: () => void
) => {
    if (!withPaperStep && !withUploader) {
        save();
    }
};

const getSizeNextStepText = (
    withPaperStep: boolean,
    withUploader: boolean | undefined,
    t: Translate
): string => {
    if (withPaperStep) {
        return t('photo_prints.options.size.next');
    } else if (!withPaperStep && withUploader) {
        return t('commons.next');
    } else {
        return t('commons.save');
    }
};

const getPaperNextStepText = (
    withBorderStep: boolean,
    withUploader: boolean | undefined,
    t: Translate
): string => {
    if (withBorderStep) {
        return t('photo_prints.options.paper.next');
    } else if (!withBorderStep && withUploader) {
        return t('commons.next');
    } else {
        return t('commons.save');
    }
};